/* ============================================================
       Arrow drawing — S-curve with endpoint dots
       Connections form a flowing path:
         Ask → See → Build → Understand → Take
       ============================================================ */
const hasDOM = typeof window !== 'undefined' && typeof document !== 'undefined';

const TABLET_BREAKPOINT = 768;
const MOBILE_LAYOUT_BREAKPOINT = 1024;
const DOT_RADIUS = 5;
const MOBILE_SAFE_INSET = 12;

let container = undefined;
let svg = undefined;
let arrowTimer;

const getViewportFlags = () => {
  const width = hasDOM ? window.innerWidth : 0;

  return {
    isTabletOrBelow: width <= TABLET_BREAKPOINT,
    isMobileLayout: width < MOBILE_LAYOUT_BREAKPOINT,
  };
};

const getConnections = () => {
  const { isTabletOrBelow } = getViewportFlags();

  if (isTabletOrBelow) {
    return [
      {
        from: 'card-ask',
        to: 'card-build',
        fromAnchor: 'right',
        toAnchor: 'right',
        curveDirection: -0.1,
        fromOffset: 0.5,
        toOffset: -0.5,
      },
      {
        from: 'card-build',
        to: 'card-see',
        fromAnchor: 'left',
        toAnchor: 'left',
        fromOffset: -0.5,
        toOffset: -0.8,
      },
      {
        from: 'card-see',
        to: 'card-understand',
        fromAnchor: 'right',
        toAnchor: 'right',
        curveDirection: -0.1,
        fromOffset: 0.7,
        toOffset: 0.6,
      },
      {
        from: 'card-understand',
        to: 'card-take',
        fromAnchor: 'left',
        toAnchor: 'left',
        curveDirection: -0.1,
        fromOffset: 0,
        toOffset: 0,
      },
    ];
  }

  return [
    {
      from: 'card-ask',
      to: 'card-see',
      fromAnchor: 'right',
      toAnchor: 'top',
      curveDirection: 0,
      fromOffset: 0,
      toOffset: 0,
    },
    {
      from: 'card-see',
      to: 'card-build',
      fromAnchor: 'right',
      toAnchor: 'bottom',
      fromOffset: 0,
      toOffset: 0,
    },
    {
      from: 'card-build',
      to: 'card-take',
      fromAnchor: 'bottom',
      toAnchor: 'top',
      curveDirection: 1,
      fromOffset: 0.7,
      toOffset: 0.6,
    },
    {
      from: 'card-understand',
      to: 'card-take',
      fromAnchor: 'right',
      toAnchor: 'left',
      curveDirection: -1,
      fromOffset: 0,
      toOffset: 0,
    },
    {
      from: 'card-understand',
      to: 'card-ask',
      fromAnchor: 'top',
      toAnchor: 'bottom',
      curveDirection: -1,
      fromOffset: -0.5,
      toOffset: -0.8,
    },
  ];
};

if (hasDOM) {
  arrowTimer = setTimeout(() => {
    if (container || svg) {
      clearTimeout(arrowTimer);
      return;
    }

    container = document.getElementById('container');
    svg = document.getElementById('arrowSvg');

    if (!container || !svg) {
      return;
    }

    const ro = new ResizeObserver(() => requestAnimationFrame(drawArrows));
    container.querySelectorAll('.card').forEach(card => ro.observe(card));
    ro.observe(container);

    window.addEventListener('resize', () => requestAnimationFrame(drawArrows));

    const mo = new MutationObserver(() => requestAnimationFrame(drawArrows));
    container.querySelectorAll('.card').forEach(card => {
      mo.observe(card, { childList: true, subtree: true, characterData: true });
    });

    requestAnimationFrame(drawArrows);
  }, 500);
}

// offset: -1 to 1, shifts anchor along the edge
// top/bottom edges → offset moves horizontally (neg=left, pos=right)
// left/right edges → offset moves vertically   (neg=up,   pos=down)
function getAnchorPoint(cardEl, side, offset) {
  const normalizedOffset = offset ?? 0;
  const rect = cardEl.getBoundingClientRect();
  const cRect = container.getBoundingClientRect();

  const rel = {
    left: rect.left - cRect.left,
    top: rect.top - cRect.top,
    right: rect.right - cRect.left,
    bottom: rect.bottom - cRect.top,
    centerX: rect.left + rect.width / 2 - cRect.left,
    centerY: rect.top + rect.height / 2 - cRect.top,
  };

  const halfW = rect.width / 2;
  const halfH = rect.height / 2;

  switch (side) {
    case 'top':
      return { x: rel.centerX + normalizedOffset * halfW, y: rel.top };
    case 'bottom':
      return { x: rel.centerX + normalizedOffset * halfW, y: rel.bottom };
    case 'left':
      return { x: rel.left, y: rel.centerY + normalizedOffset * halfH };
    case 'right':
      return { x: rel.right, y: rel.centerY + normalizedOffset * halfH };
    default:
      return undefined;
  }
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function clampPoint(point, bounds) {
  return {
    x: clamp(point.x, bounds.minX, bounds.maxX),
    y: clamp(point.y, bounds.minY, bounds.maxY),
  };
}

function parsePixelValue(value) {
  const numeric = parseFloat(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function getInlineSafeInset() {
  if (!hasDOM || !container) {
    return MOBILE_SAFE_INSET;
  }

  const styles = window.getComputedStyle(container);
  const startInset = parsePixelValue(styles.paddingInlineStart || styles.paddingLeft);
  const endInset = parsePixelValue(styles.paddingInlineEnd || styles.paddingRight);
  const containerInset = Math.max(0, Math.min(startInset, endInset) - 2);

  return Math.max(MOBILE_SAFE_INSET, containerInset);
}

/* ----------------------------------------------------------
       Auto-distribute: when multiple connectors attach to the
       same card+side, evenly space them along that edge so they
       never overlap. Uses the range [-0.5 … +0.5] for spacing.
       ---------------------------------------------------------- */
function computeAutoOffsets(connections) {
  const groups = {};

  connections.forEach((conn, i) => {
    const fromEl = document.getElementById(conn.from);
    const toEl = document.getElementById(conn.to);
    if (!fromEl || !toEl) return;
    if (fromEl.style.display === 'none' || toEl.style.display === 'none') return;

    const fKey = conn.from + '|' + conn.fromAnchor;
    const tKey = conn.to + '|' + conn.toAnchor;

    if (!groups[fKey]) groups[fKey] = [];
    groups[fKey].push({ connIndex: i, role: 'from' });

    if (!groups[tKey]) groups[tKey] = [];
    groups[tKey].push({ connIndex: i, role: 'to' });
  });

  const autoOffsets = connections.map(() => ({ fromOffset: 0, toOffset: 0 }));
  const SPREAD = 0.6;

  Object.values(groups).forEach(items => {
    const n = items.length;
    items.forEach((item, idx) => {
      const offset = n === 1 ? 0 : -SPREAD / 2 + (SPREAD / (n - 1)) * idx;

      if (item.role === 'from') {
        autoOffsets[item.connIndex].fromOffset =
          connections[item.connIndex].fromOffset !== undefined
            ? connections[item.connIndex].fromOffset
            : offset;
      } else {
        autoOffsets[item.connIndex].toOffset =
          connections[item.connIndex].toOffset !== undefined
            ? connections[item.connIndex].toOffset
            : offset;
      }
    });
  });

  return autoOffsets;
}

function drawArrows() {
  if (!container || !svg) {
    return;
  }

  while (svg.firstChild) svg.removeChild(svg.firstChild);

  const containerWidth = container.clientWidth;
  const containerHeight = container.scrollHeight;

  svg.style.height = `${containerHeight}px`;
  svg.setAttribute('width', `${containerWidth}`);
  svg.setAttribute('height', `${containerHeight}`);
  svg.setAttribute('viewBox', `0 0 ${containerWidth} ${containerHeight}`);
  svg.setAttribute('preserveAspectRatio', 'none');

  const activeConnections = getConnections();
  const autoOffsets = computeAutoOffsets(activeConnections);
  const { isMobileLayout } = getViewportFlags();
  const safeInsetX = isMobileLayout ? getInlineSafeInset() : 0;
  const safeInsetY = isMobileLayout ? MOBILE_SAFE_INSET : 0;
  const safeBounds = {
    minX: DOT_RADIUS + safeInsetX,
    maxX: containerWidth - DOT_RADIUS - safeInsetX,
    minY: DOT_RADIUS + safeInsetY,
    maxY: containerHeight - DOT_RADIUS - safeInsetY,
  };

  activeConnections.forEach((conn, i) => {
    const fromEl = document.getElementById(conn.from);
    const toEl = document.getElementById(conn.to);
    if (!fromEl || !toEl) return;
    if (fromEl.style.display === 'none' || toEl.style.display === 'none') return;

    let start = getAnchorPoint(fromEl, conn.fromAnchor, autoOffsets[i].fromOffset);
    let end = getAnchorPoint(toEl, conn.toAnchor, autoOffsets[i].toOffset);
    if (!start || !end) return;

    if (isMobileLayout) {
      start = clampPoint(start, safeBounds);
      end = clampPoint(end, safeBounds);
    }

    const dx = Math.abs(end.x - start.x);
    const dy = Math.abs(end.y - start.y);
    const dist = Math.sqrt(dx * dx + dy * dy);
    const mobileMaxOffset = Math.max(containerWidth * 0.25, 40);
    const rawOffset = Math.max(dist * 0.5, 50);
    const offset = isMobileLayout ? Math.min(rawOffset, mobileMaxOffset) : rawOffset;

    let cp1 = {};
    let cp2 = {};

    const baseBow = Math.max(dist * 0.25, 30);
    const dir = conn.curveDirection !== undefined ? conn.curveDirection : 1;
    const curveBow = baseBow * dir;

    function perpShift(anchor) {
      if (anchor === 'top' || anchor === 'bottom') return { x: curveBow, y: 0 };
      return { x: 0, y: -curveBow };
    }

    const pFrom = perpShift(conn.fromAnchor);
    const pTo = perpShift(conn.toAnchor);

    switch (conn.fromAnchor) {
      case 'bottom':
        cp1 = { x: start.x + pFrom.x, y: start.y + offset };
        break;
      case 'top':
        cp1 = { x: start.x + pFrom.x, y: start.y - offset };
        break;
      case 'right':
        cp1 = { x: start.x + offset, y: start.y + pFrom.y };
        break;
      case 'left':
        cp1 = { x: start.x - offset, y: start.y + pFrom.y };
        break;
      default:
        return;
    }

    switch (conn.toAnchor) {
      case 'left':
        cp2 = { x: end.x - offset, y: end.y + pTo.y };
        break;
      case 'right':
        cp2 = { x: end.x + offset, y: end.y + pTo.y };
        break;
      case 'top':
        cp2 = { x: end.x + pTo.x, y: end.y - offset };
        break;
      case 'bottom':
        cp2 = { x: end.x + pTo.x, y: end.y + offset };
        break;
      default:
        return;
    }

    if (isMobileLayout) {
      cp1 = clampPoint(cp1, safeBounds);
      cp2 = clampPoint(cp2, safeBounds);
    }

    const d = `M ${start.x} ${start.y} C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${end.x} ${end.y}`;
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', d);
    svg.appendChild(path);

    [start, end].forEach(pt => {
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', pt.x);
      circle.setAttribute('cy', pt.y);
      circle.setAttribute('r', DOT_RADIUS);
      svg.appendChild(circle);
    });
  });
}
